import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Form, Input, message } from "antd";
import { LockOutlined, SaveOutlined, UnlockOutlined } from "@ant-design/icons";

import { setMenu } from "../../redux/reducers/navigationSlice";
import { setUserAccount } from "../../redux/reducers/userAccountSlice";
import AuthAPI from "../../api/AuthAPI";
import SettingsAPI from "../../api/SettingsAPI";

import "./settings.styles.css";

const SettingsPage = () => {
  const dispatch = useDispatch();
  const { name, token } = useSelector((state) => state.userAccount.user);
  const [messageApi, messageContext] = message.useMessage();
  const [unlockForm] = Form.useForm();
  const [settingsForm] = Form.useForm();

  const [isLoadingUnlockForm, setIsLoadingUnlockForm] = useState(false);
  const [isDisabled, setIsDisabled] = useState(true);
  const [settingsIsLoading, setSettingsIsLoading] = useState(false);

  const loadSettings = useCallback(async () => {
    messageApi.open({ type: "loading", content: "Cargando..." });

    try {
      const response = await SettingsAPI.getSettings(token);
      const settingsResponse = response.data;
      const {
        GuestAccessDuration,
        ResidentialName,
        ResidentialAddress,
        ResidentialGeoPos,
      } = settingsResponse.settings;
      settingsForm.setFieldValue("guestAccessDuration", GuestAccessDuration);
      settingsForm.setFieldValue("residentialName", ResidentialName);
      settingsForm.setFieldValue("residentialAddress", ResidentialAddress);
      settingsForm.setFieldValue(
        "residentialGeoPosLat",
        ResidentialGeoPos ? ResidentialGeoPos.split(",")[0] : ""
      );
      settingsForm.setFieldValue(
        "residentialGeoPosLong",
        ResidentialGeoPos ? ResidentialGeoPos.split(",")[1] : ""
      );
      messageApi.destroy();
    } catch (error) {
      let message = "Servicio no disponible, intente más tarde. ";

      if (error.code !== "ERR_NETWORK") {
        message = "Contraseña no válida.";
        setUserAccount(null);
      }

      messageApi.open({
        type: "error",
        content: message,
      });
    }
  }, [messageApi, settingsForm, token]);

  const onFinishUnlockForm = async ({ unlockPassword }) => {
    setIsLoadingUnlockForm(true);

    try {
      const response = await AuthAPI.authUser(name, unlockPassword);
      const authResponse = response.data;

      if (!authResponse.success) {
        messageApi.open({ type: "error", content: "Clave no válida." });
        return;
      }

      setIsDisabled(false);
    } catch (error) {
      let message = "Servicio no disponible, intente más tarde. ";

      if (error.code !== "ERR_NETWORK") {
        message = "Contraseña no válida.";
      }

      messageApi.open({
        type: "error",
        content: message,
      });
    }

    unlockForm.resetFields();
    setIsLoadingUnlockForm(false);
  };

  const onFinishUpdateForm = async ({
    guestAccessDuration,
    residentialName,
    residentialAddress,
    residentialGeoPosLat,
    residentialGeoPosLong,
  }) => {
    setSettingsIsLoading(true);

    try {
      const response = await SettingsAPI.saveSettings(
        token,
        guestAccessDuration,
        residentialName,
        residentialAddress,
        residentialGeoPosLat + "," + residentialGeoPosLong
      );
      const saveSettingsResponse = response.data;

      if (!saveSettingsResponse.success) {
        messageApi.open({
          type: "error",
          content: saveSettingsResponse.message,
        });
        return;
      }

      messageApi.open({
        type: "success",
        content: saveSettingsResponse.message,
      });
    } catch (error) {
      let message = "Servicio no disponible, intente más tarde. ";

      if (error.code !== "ERR_NETWORK") {
        message = error.response.data.message;

        if (error.response.status === 401) {
          dispatch(setUserAccount(null));
        }
      }

      messageApi.open({
        type: "error",
        content: message,
      });
    }

    setSettingsIsLoading(false);
  };

  useEffect(() => {
    dispatch(setMenu("settings"));
    loadSettings();
  }, [dispatch, loadSettings]);

  return (
    <div className="configuration-page">
      {messageContext}
      <h2 style={{ padding: "0.5em" }}>Configuraciones Generales</h2>
      <div>
        <Form
          form={unlockForm}
          onFinish={onFinishUnlockForm}
          layout="inline"
          style={{ padding: "10px" }}
        >
          {isDisabled ? (
            <Form.Item
              label="Confirme con la clave de su cuenta para habilitar"
              name="unlockPassword"
              rules={[{ required: true, message: "Requerido" }]}
              required
            >
              <Input type="password" required />
            </Form.Item>
          ) : null}
          <Form.Item>
            <Button
              onClick={() => {
                if (!isDisabled) {
                  setIsDisabled(true);
                  loadSettings();
                  return;
                }

                unlockForm.submit();
              }}
              type={isDisabled ? "primary" : "default"}
              loading={isLoadingUnlockForm}
              icon={isDisabled ? <LockOutlined /> : <UnlockOutlined />}
            >
              {isDisabled ? "Habilitar" : "Deshabilitar"}
            </Button>
          </Form.Item>
        </Form>
      </div>
      <div style={{ width: "500px" }}>
        <Form
          onFinish={onFinishUpdateForm}
          form={settingsForm}
          disabled={isDisabled}
          layout="vertical"
        >
          <Form.Item
            label="Nombre de residencial"
            name="residentialName"
            required
          >
            <Input required />
          </Form.Item>
          <Form.Item
            label="Dirección del residencial"
            name="residentialAddress"
            required
          >
            <Input required />
          </Form.Item>
          <Form.Item
            label="Geolocalización del residencial"
            style={{ marginBottom: "-5px" }}
            required
          >
            <Form.Item
              name="residentialGeoPosLat"
              style={{ display: "inline-block" }}
              required
            >
              <Input placeholder="Latitude" required />
            </Form.Item>
            <Form.Item
              name="residentialGeoPosLong"
              style={{ display: "inline-block", margin: "0px 8px" }}
              required
            >
              <Input placeholder="Longitud" required />
            </Form.Item>
          </Form.Item>
          <Form.Item
            label="Duración de acceso de invitado (en segs.)"
            name="guestAccessDuration"
            required
          >
            <Input required />
          </Form.Item>
          <div style={{ textAlign: "center" }}>
            <Button type="primary" htmlType="submit" icon={<SaveOutlined />}>
              Guardar
            </Button>
          </div>
        </Form>
      </div>
    </div>
  );
};

export default SettingsPage;
